import { createSelector } from '@reduxjs/toolkit'

import { DEFAULT_COUNTRY_ISO_CODE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { getPaymentFormSponsoringCompanies } from '../../common/utils/utils'
import { RootState } from '../store'

const getRequestPaymentState = (state: RootState) => state?.requestPayment

export const getRequestPaymentCustomers = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.customers,
)

export const getRequestPaymentCustomersForDisplay = createSelector(getRequestPaymentCustomers, (customers) =>
  customers?.filter((customer) => customer.is_subpopulation !== true),
)

/** Sponsoring company options for the payment request form */
export const getRequestPaymentFormSponsoringCompanies = createSelector(
  getRequestPaymentCustomersForDisplay,
  getPaymentFormSponsoringCompanies,
)
export const getRequestPaymentCustomersByCountry = createSelector(
  [getRequestPaymentState],
  (requestPaymentState) => requestPaymentState?.customersByCountry,
)

export const getRequestPaymentCustomersByCountryFetchFailed = createSelector(
  [getRequestPaymentState],
  (requestPaymentState) => requestPaymentState?.customersByCountryFetchFailed,
)

export const getRequestPaymentSelectedProvider = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.selectedProvider,
)

export const getRequestPaymentRates = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.rates,
)

export const getRequestPaymentDiffPricingEnabled = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.providerDiffPricingInfo?.differential_pricing_enabled,
)

export const getRequestPaymentDiffPricingModifier = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.providerDiffPricingInfo?.differential_pricing_in_person_modifier,
)

export const getRequestPaymentProvidersList = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.providersList,
)

export const getRequestPaymentCopy = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.copy,
)

export const getRequestPaymentPatient = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.patient,
)

const getRequestPaymentData = createSelector(getRequestPaymentState, (requestPaymentState) => requestPaymentState?.data)

export const getRequestPaymentDataSessionTrackingStatus = createSelector(
  getRequestPaymentData,
  (requestPaymentData) => requestPaymentData?.session_tracking_status,
)

export const getRequestPaymentDataOutcomeReminderStatus = createSelector(
  getRequestPaymentData,
  (requestPaymentData) => requestPaymentData?.outcome_reminder_status,
)

const getRequestPaymentResponse = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.response,
)

const getRequestPaymentResponseData = createSelector(
  getRequestPaymentResponse,
  (requestPaymentResponse) => requestPaymentResponse?.data,
)

export const getRequestPaymentResponseDataSessionTrackingStatus = createSelector(
  getRequestPaymentResponseData,
  (requestPaymentResponseData) => requestPaymentResponseData?.session_tracking_status,
)

export const getRequestPaymentShowDuplicateModal = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.showDuplicateModal,
)

export const getRequestPaymentShowCancelledSessionsExceededModal = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.showCancelledSessionsExceededModal,
)

export const getRequestPaymentDataData = createSelector(
  getRequestPaymentData,
  (requestPaymentData) => requestPaymentData?.data,
)

export const getRequestPaymentLyraCodeValidationInfo = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.lyraCodeValidationInfo,
)

export const getRequestPaymentShowIneligibleClientModal = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.showIneligibleClientModal,
)

export const getRequestPaymentCustomerSessionTypes = createSelector(
  [getRequestPaymentState, (_, customer: string) => customer],
  (requestPaymentState, customer) => requestPaymentState?.customerSessionTypes?.[customer],
)

export const getRequestPaymentEligibilityStatus = createSelector(
  getRequestPaymentState,
  (requestPaymentState) => requestPaymentState?.eligibility?.status,
)

export const getRequestPaymentCustomerProgramCoverageBreakdown = createSelector(
  [getRequestPaymentState, (_, customer?: string, country?: string) => ({ customer, country })],
  (requestPaymentState, { customer, country }) =>
    !!customer
      ? requestPaymentState?.customerProgramCoverageBreakdown?.[country ?? DEFAULT_COUNTRY_ISO_CODE]?.[customer]
      : undefined,
)
